import { useState, useMemo, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Trans, useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import NextButton from "../components/NextButton";
import type { StepComponentType } from "../../hooks/IncorporationWizardFormContext";
import { useIncorporationWizardFormCtx } from "../../hooks/IncorporationWizardFormContext";
import TextInput from "../../components/TextInput";
import PhoneInput from "../../components/PhoneInput";
import SelectInput from "../../components/SelectInput";
import {
  GESTION_DIRECTORES,
  USER_TYPE,
  SI_NO_OPTIONS,
  type IncorporationWizardInitialData,
  type UserTypeOption,
  CARGO_DIRECTOR,
} from "@/schemas/incorporation";
import RadioGroup from "../../components/RadioGroup";
import { CustomModal } from "@/components/CustomModal";
import countriesData from "../../components/countries.json";
import CheckboxInput from "../../components/CheckboxInput";
import { ContactCard } from "../../components/ContactCard";
import {
  createOrUpdateIncorporationContact,
  deleteIncorporationContact,
  IncorporationContactError,
} from "@/services/incorporationContact";
import { CustomTooltip } from "../../CustomTooltip";
import { type ContactFormValues, contactFormSchema } from "../../schema";
import { isDirectorContact } from "../../contact-helper";
import z from "zod";

const directorsStepSchema = z
  .object({
    gestion_directores: z.enum(GESTION_DIRECTORES, {
      message: "directors.gestion_directores.required",
    }),
    // we validate contacts if is not abogado managed
    contacts: z.array(contactFormSchema).optional(),
    // Include user_type so we can branch validations
    user_type: z.enum(USER_TYPE).optional(),
  })
  .superRefine((data, ctx) => {
    const { contacts, user_type, gestion_directores } = data;
    const isIntermediary = user_type === USER_TYPE[1]; // "Intermediario"
    const isAbogadoManaged = gestion_directores === GESTION_DIRECTORES[0]; // "abogado"

    // ONLY required when gestion_directores is "cliente", NOT for "abogado"
    // para abogado se hara siguiente paso y agregara accionistas
    if (!isAbogadoManaged) {
      if (!contacts) {
        ctx.addIssue({
          code: "custom",
          path: ["contacts"],
          message: "directors.validation.min_contacts",
        });
        return;
      }

      // 0) At least 3 contacts (directors/shareholders)
      if (contacts.length < 3) {
        ctx.addIssue({
          code: "custom",
          path: ["contacts"],
          message: "directors.validation.min_three_contacts",
        });
      }
      // 1) At least one soy_quien_completa_registro
      //    ONLY required for "Miembro", NOT for "Intermediario"
      if (!isIntermediary) {
        const hasCompleter =
          contacts &&
          contacts.some((c) => c?.soy_quien_completa_registro === true);
        if (!hasCompleter) {
          ctx.addIssue({
            code: "custom",
            path: ["contacts"],
            message: "directors.validation.completer_required",
          });
        }
      }

      // 2) Director cargos must be unique (except "no_tendra_cargo")
      const usedCargos = new Set<string>();
      for (const contact of contacts) {
        if (!isDirectorContact(contact)) continue;
        const cargo = contact.cargo;
        if (!cargo || cargo === CARGO_DIRECTOR[3]) continue;
        if (usedCargos.has(cargo)) {
          ctx.addIssue({
            code: "custom",
            path: ["contacts"],
            message: "directors.validation.duplicate_cargo",
          });
          break;
        }
        usedCargos.add(cargo);
      }
      usedCargos.delete(CARGO_DIRECTOR[3]);
      if (usedCargos.size !== 3) {
        ctx.addIssue({
          code: "custom",
          path: ["contacts"],
          message: "directors.validation.min_three_different_directors",
        });
      }

      // 3) Sum of porcentaje_beneficio cannot exceed 100%
      const totalPercentage = contacts.reduce(
        (sum, c) => sum + (c?.porcentaje_beneficio || 0),
        0
      );
      if (totalPercentage > 100 + 0.01) {
        ctx.addIssue({
          code: "custom",
          path: ["contacts"],
          message: "directors.validation.percentage_exceeds_100",
        });
      }
    }
  });

function DirectorsForm({
  initialData,
  userType,
  onSave,
  onCancel,
  hideSoyQuienCompletaRegistro = false,
  disabledCargos = [],
}: {
  initialData?: ContactFormValues;
  userType?: UserTypeOption;
  onSave: (contact: ContactFormValues) => void;
  onCancel: () => void;
  hideSoyQuienCompletaRegistro?: boolean;
  disabledCargos?: string[];
}) {
  const { t, i18n } = useTranslation();
  const isMember = userType === USER_TYPE[0]; // "Miembro"
  const { control, handleSubmit, watch, setValue } = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: initialData || {
      contact_type: "persona natural",
      rol: ["director"],
      nombre: "",
      email: "",
      telefono: "",
      direccion: "",
      nacionalidades: [],
      nacionalidad_principal: "",
      cargo: "",
      porcentaje_beneficio: 0,
      residencia_fiscal: "",
      n_certificados: "",
      soy_quien_completa_registro: false,
      es_beneficiario: undefined,
    },
  });

  const contactType = watch("contact_type");
  const selectedNationalities = watch("nacionalidades") || [];
  const isShareholderValue = watch("es_beneficiario");
  const isShareholder = isShareholderValue === SI_NO_OPTIONS[0]; // "si"

  // Auto-set nacionalidad_principal when exactly 1 nationality is selected
  useEffect(() => {
    if (selectedNationalities.length === 1) {
      setValue("nacionalidad_principal", selectedNationalities[0]);
    }
  }, [selectedNationalities, setValue]);

  const onSubmit = (formData: ContactFormValues) => {
    onSave(formData);
  };

  const cargoOptions = [
    {
      value: CARGO_DIRECTOR[0],
      label: t("directors.contact_form.cargo_options.presidente"),
    },
    {
      value: CARGO_DIRECTOR[1],
      label: t("directors.contact_form.cargo_options.secretario"),
    },
    {
      value: CARGO_DIRECTOR[2],
      label: t("directors.contact_form.cargo_options.tesorero"),
    },
  ];

  const countryOptions = countriesData.map((country) => ({
    value: country.name_es,
    label: i18n.language === "es" ? country.name_es : country.name_en,
  }));

  const shouldHideSoyQuienCompletaRegistro =
    !isMember ||
    (!initialData?.soy_quien_completa_registro && hideSoyQuienCompletaRegistro);

  return (
    <form
      onSubmit={handleSubmit(onSubmit, (errors) => {
        console.log(errors);
      })}
    >
      <div className="d-flex flex-column gap-3">
        {/* Show form only when persona natural is selected */}
        {contactType === "persona natural" && (
          <>
            <h5 className="fw-semibold size-18 mt-3">
              {t("directors.contact_form.persona_natural.title")}
            </h5>
            <TextInput
              name="nombre"
              control={control}
              label={t("directors.contact_form.nombre.label")}
              placeholder={t("directors.contact_form.nombre.placeholder")}
              helperText={t("directors.contact_form.nombre.helper_text")}
            />

            {/* Soy quien completa registro */}
            {!shouldHideSoyQuienCompletaRegistro && (
              <CheckboxInput
                name="soy_quien_completa_registro"
                control={control}
                label={t(
                  "directors.contact_form.soy_quien_completa_registro.label"
                )}
              />
            )}

            <TextInput
              name="email"
              control={control}
              label={t("directors.contact_form.email.label")}
              placeholder={t("directors.contact_form.email.placeholder")}
            />

            <TextInput
              name="direccion"
              control={control}
              label={t("directors.contact_form.direccion.label")}
            />

            <PhoneInput
              name="telefono"
              control={control}
              label={t("directors.contact_form.telefono.label")}
            />

            <SelectInput
              name="nacionalidades"
              control={control}
              label={t("directors.contact_form.nacionalidades.label")}
              placeholder={t(
                "directors.contact_form.nacionalidades.placeholder"
              )}
              options={countryOptions}
              isMulti={true}
            />

            {selectedNationalities.length > 1 && (
              <SelectInput
                name="nacionalidad_principal"
                control={control}
                label={t("directors.contact_form.nacionalidad_principal.label")}
                placeholder={t(
                  "directors.contact_form.nacionalidad_principal.placeholder"
                )}
                options={countryOptions.filter((option) =>
                  selectedNationalities.includes(option.value)
                )}
                isMulti={false}
              />
            )}

            <div>
              <label className="form-label d-flex align-items-center gap-1">
                <span>
                  {t("directors.contact_form.residencia_fiscal.label")}
                </span>
                <CustomTooltip
                  text={t("directors.contact_form.residencia_fiscal.tooltip")}
                />
              </label>
              <SelectInput
                name="residencia_fiscal"
                control={control}
                placeholder={t(
                  "directors.contact_form.residencia_fiscal.placeholder"
                )}
                options={countryOptions}
                isMulti={false}
              />
            </div>

            {/* Cargo del director - only show if director role is selected */}
            <div>
              <h5 className="fw-semibold size-18 mt-3">
                {t("directors.contact_form.cargo.title")}
              </h5>
              <SelectInput
                name="cargo"
                control={control}
                label={t("directors.contact_form.cargo.label")}
                placeholder={t("directors.contact_form.cargo.placeholder")}
                options={cargoOptions}
                isMulti={false}
                isOptionDisabled={(option) =>
                  option.value !== CARGO_DIRECTOR[3] &&
                  disabledCargos.includes(option.value)
                }
              />
            </div>

            {/* ¿Es beneficiario o accionista? */}
            <div style={{ maxWidth: 250 }}>
              <label className="form-label mb-2">
                {t("directors.contact_form.es_beneficiario.label")}
              </label>
              <RadioGroup
                name="es_beneficiario"
                control={control}
                options={[
                  {
                    value: SI_NO_OPTIONS[0], // "si"
                    label: t("directors.contact_form.es_beneficiario.si"),
                  },
                  {
                    value: SI_NO_OPTIONS[1], // "no"
                    label: t("directors.contact_form.es_beneficiario.no"),
                  },
                ]}
                layout="horizontal"
                variant="button"
              />
            </div>

            {/* Participación accionaria - only show when "si" is selected */}
            {isShareholder && (
              <div
                className="d-flex flex-column gap-3"
                style={{ maxWidth: 250 }}
              >
                <TextInput
                  name="porcentaje_beneficio"
                  control={control}
                  label={t("directors.contact_form.porcentaje_beneficio.label")}
                  placeholder={t(
                    "directors.contact_form.porcentaje_beneficio.placeholder"
                  )}
                  type="number"
                />
              </div>
            )}
          </>
        )}

        {/* Action buttons */}
        <div className="d-flex justify-content-end gap-2 mt-3">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onCancel}
          >
            {t("directors.contact_form.buttons.cancel")}
          </button>
          <button type="submit" className="btn btn-black">
            {t("directors.contact_form.buttons.save")}
          </button>
        </div>
      </div>
    </form>
  );
}

/**
 * DirectorsStep - Contact management only
 * This step is only shown when gestion_directores === "cliente"
 * The radio selection is now handled by GestionDirectoresStep
 */
const DirectorsStep: StepComponentType = () => {
  const { t } = useTranslation();
  const { data, setData, next } = useIncorporationWizardFormCtx();

  const gestionDirectores = data?.incorporationRequest?.gestion_directores;

  // Transform contacts from global state to form values (porcentaje_beneficio as number)
  const directorContacts = useMemo<ContactFormValues[]>(
    () =>
      (data?.incorporationContact || [])
        .filter((contact) => isDirectorContact(contact)) // only directors
        .map((contact) => ({
          ...contact,
          porcentaje_beneficio: contact.porcentaje_beneficio || 0,
        })),
    [data?.incorporationContact]
  );

  const [validationError, setValidationError] = useState<string | null>(null);
  const [contactError, setContactError] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // Auto-open modal when there are no director contacts
  useEffect(() => {
    if (directorContacts.length === 0) {
      setIsAddModalOpen(true);
    }
  }, [directorContacts.length]);
  console.log({contacts: data?.incorporationContact});
  // Handle edit contact - update via API
  // Uses contact ID to find the correct index in the full contacts array
  const handleEditContact =
    (contactId: string | number | undefined) => async (contactData: ContactFormValues) => {
      setContactError(null);

      try {
        const incorporationRequestId = data?.incorporationRequest?.id;

        if (!incorporationRequestId) {
          throw new Error(
            "La solicitud de incorporación no ha sido creada. Por favor, complete el paso anterior."
          );
        }

        const existingContacts = data?.incorporationContact || [];
        // Find the actual index in the full contacts array using the contact ID
        const actualIndex = existingContacts.findIndex((c) => c.id === contactId);
        const existingContact = actualIndex >= 0 ? existingContacts[actualIndex] : undefined;

        const requestData = {
          ...contactData,
          incorporation_request_id: incorporationRequestId,
          ...(existingContact?.id && { id: existingContact.id }),
        };

        const response = await createOrUpdateIncorporationContact(requestData);
        setData((prevData) => {
          if (!prevData) return prevData;
          const updatedContacts = [...(prevData.incorporationContact || [])];
          // Find the actual index again in case state changed
          const indexToUpdate = updatedContacts.findIndex((c) => c.id === contactId);
          if (indexToUpdate >= 0) {
            updatedContacts[indexToUpdate] = response.data;
          }

          return {
            ...prevData,
            incorporationRequest: prevData.incorporationRequest || {},
            incorporationContact: updatedContacts,
            currentUser: prevData.currentUser,
            incorporationStripePrice: prevData.incorporationStripePrice,
            path: prevData.path,
          };
        });
      } catch (error) {
        if (error instanceof IncorporationContactError) {
          const errorMessage =
            error.errors && error.errors.length > 0
              ? error.errors.join(", ")
              : error.message;
          setContactError(errorMessage);
        } else {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Error al guardar el contacto";
          setContactError(errorMessage);
        }
      }
    };

  // Handle append contact - create via API
  const handleAppendContact = async (contactData: ContactFormValues) => {
    setContactError(null);

    try {
      const incorporationRequestId = data?.incorporationRequest?.id;

      if (!incorporationRequestId) {
        throw new Error(
          "La solicitud de incorporación no ha sido creada. Por favor, complete el paso anterior."
        );
      }

      const requestData = {
        ...contactData,
        incorporation_request_id: incorporationRequestId,
      };

      const response = await createOrUpdateIncorporationContact(requestData);

      setData((prevData) => {
        if (!prevData) {
          if (!data?.incorporationStripePrice || !data?.path) {
            console.warn(
              "Cannot create incorporation data without required Stripe fields"
            );
            return prevData;
          }
          return {
            incorporationRequest: data.incorporationRequest || {},
            incorporationContact: [response.data],
            incorporationStripePrice: data.incorporationStripePrice,
            path: data.path,
            currentUser: data.currentUser,
          };
        }
        return {
          ...prevData,
          incorporationRequest: prevData.incorporationRequest || {},
          incorporationContact: [
            ...(prevData.incorporationContact || []),
            response.data,
          ],
          currentUser: prevData.currentUser,
          incorporationStripePrice: prevData.incorporationStripePrice,
          path: prevData.path,
        };
      });
    } catch (error) {
      if (error instanceof IncorporationContactError) {
        const errorMessage =
          error.errors && error.errors.length > 0
            ? error.errors.join(", ")
            : error.message;
        setContactError(errorMessage);
      } else {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Error al guardar el contacto";
        setContactError(errorMessage);
      }
    }
  };

  // Handle delete contact - call backend and, on success, remove from local state
  // Uses contact ID to find and delete the correct contact
  const handleDeleteContact = async (contactId: string | number | undefined) => {
    setContactError(null);

    const existingContacts = data?.incorporationContact || [];
    const contactToDelete = existingContacts.find((c) => c.id === contactId);

    if (contactToDelete?.id) {
      try {
        await deleteIncorporationContact(contactToDelete.id);
      } catch (error) {
        if (error instanceof IncorporationContactError) {
          const errorMessage =
            error.errors && error.errors.length > 0
              ? error.errors.join(", ")
              : error.message;
          setContactError(errorMessage);
        } else {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Error al eliminar el contacto";
          setContactError(errorMessage);
        }
        return;
      }
    }

    setData((prevData) => {
      if (!prevData) return prevData;
      const updatedContacts = (prevData.incorporationContact || []).filter(
        (contact) => contact.id !== contactId
      );
      return {
        ...prevData,
        incorporationRequest: prevData.incorporationRequest || {},
        incorporationContact: updatedContacts,
        currentUser: prevData.currentUser,
        incorporationStripePrice: prevData.incorporationStripePrice,
        path: prevData.path,
      };
    });
  };

  // Handle final submit - validate and proceed to next step
  const handleSubmit = () => {
    setValidationError(null);

    // Validate using schema with current global state
    const result = directorsStepSchema.safeParse({
      gestion_directores: gestionDirectores,
      contacts: (data?.incorporationContact || []).map((c) => ({
        ...c,
        porcentaje_beneficio: c.porcentaje_beneficio || 0,
      })),
      user_type: data?.incorporationRequest?.user_type,
    });

    if (!result.success) {
      const error = result.error.issues[0];
      setValidationError(
        t(error?.message) || t("directors.validation.validation_error")
      );
      return;
    }

    next();
  };

  // Check ALL contacts (not just directors) for soy_quien_completa_registro
  // Only one contact across the entire form can have this checked
  const shouldHideSoyQuienCompletaRegistro = (data?.incorporationContact || []).some(
    (contact) => contact.soy_quien_completa_registro
  );

  const getDisabledCargos = (excludeIndex?: number): string[] => {
    const used = new Set<string>();
    directorContacts.forEach((contact, idx) => {
      if (typeof excludeIndex === "number" && idx === excludeIndex) return;
      if (!isDirectorContact(contact)) return;
      const cargo = contact.cargo;
      if (!cargo || cargo === CARGO_DIRECTOR[3]) return;
      used.add(cargo);
    });
    return Array.from(used);
  };

  // Determine if we already have all 3 required director positions
  const hasAllThreeDirectors = useMemo(() => {
    const requiredCargos = ["presidente", "secretario", "tesorero"];
    const directorCargos = new Set<string>();

    directorContacts.forEach((contact) => {
      if (!isDirectorContact(contact)) return;
      const cargo = contact.cargo;
      if (!cargo || cargo === CARGO_DIRECTOR[3]) return;
      directorCargos.add(cargo);
    });

    return requiredCargos.every((cargo) => directorCargos.has(cargo));
  }, [directorContacts]);

  const canAddMoreContacts = !hasAllThreeDirectors;

  const modalTitle = t("directors.contact_form.title_add_directors");

  return (
    <Layout
      title={t("directors.title")}
      description={<Trans i18nKey="directors.description" />}
      maxWidth={950}
    >
      <div className="d-flex flex-column gap-4 text-start mt-4">
        {/* Display list of director contacts */}
        {directorContacts.length > 0 && (
          <div className="row g-3">
            {directorContacts.map((contact, index) => (
              <ContactCard
                key={contact?.id || contact.email}
                contact={contact}
                modalTitle={modalTitle}
                onDelete={() => handleDeleteContact(contact.id)}
                renderForm={(handlers) => (
                  <DirectorsForm
                    initialData={contact}
                    hideSoyQuienCompletaRegistro={
                      shouldHideSoyQuienCompletaRegistro
                    }
                    userType={data?.incorporationRequest?.user_type}
                    disabledCargos={getDisabledCargos(index)}
                    onSave={(contactData: ContactFormValues) => {
                      handleEditContact(contact.id)(contactData);
                      handlers.onClose();
                    }}
                    onCancel={handlers.onClose}
                  />
                )}
              />
            ))}
          </div>
        )}

        {/* Add directors section */}
        <div className="d-flex flex-column gap-3">
          {directorContacts.length > 0 && <hr />}
          {canAddMoreContacts && (
            <>
              <button
                type="button"
                className="btn btn-gray-1000 px-4 py-3 my-2 w-100 text-start font-weight-600"
                onClick={() => setIsAddModalOpen(true)}
              >
                +&nbsp;
                {t("directors.add_contact.button_add_directors")}
              </button>
              <CustomModal
                title={modalTitle}
                size="md"
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
              >
                <DirectorsForm
                  userType={data?.incorporationRequest?.user_type}
                  hideSoyQuienCompletaRegistro={
                    shouldHideSoyQuienCompletaRegistro
                  }
                  disabledCargos={getDisabledCargos()}
                  onSave={(contactData: ContactFormValues) => {
                    handleAppendContact(contactData);
                    setIsAddModalOpen(false);
                  }}
                  onCancel={() => setIsAddModalOpen(false)}
                />
              </CustomModal>
            </>
          )}

          {/* Show validation errors */}
          {validationError && (
            <div className="text-danger">{validationError}</div>
          )}

          {/* Show contact errors */}
          {contactError && (
            <div className="alert alert-danger" role="alert">
              {contactError}
            </div>
          )}
        </div>

        {/* Continue Button */}
        <div className="d-flex justify-content-center w-100 w-md-50 mx-auto">
          <NextButton
            type="button"
            label={t("company_name.continue")}
            onClick={handleSubmit}
          />
        </div>
      </div>
    </Layout>
  );
};

DirectorsStep.title = (t) => t("directors.title");
DirectorsStep.description = (t) => t("directors.description_progress_panel");
DirectorsStep.isComplete = (data: IncorporationWizardInitialData) => {
  const schemaData = {
    gestion_directores: data.incorporationRequest?.gestion_directores,
    contacts: data.incorporationContact,
    user_type: data.incorporationRequest?.user_type,
  };
  const result = directorsStepSchema.safeParse(schemaData);
  return result.success;
};

export default DirectorsStep;
